import { useEffect, useState } from 'react';
import { api } from '../../api.js';
import { Modal } from '../ui/modal.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../ui/table.js';
import { Loading } from '../Loading.js';
import { fmtNum } from '../../utils.js';
import type { BatchFile } from './pdfBatchApi.js';

/**
 * The document and what was read from it, side by side.
 *
 * A total of 46,748,058 means nothing on its own — it means something next to the
 * page it was taken from. This is where "בדקו לפני פרסום" stops being a slogan:
 * every sleeve and every security is listed, against the PDF itself.
 */

const SLEEVE_HE: Record<string, string> = {
  stocks_il: 'מניות ישראל',
  stocks_foreign: 'מניות חו"ל',
  commodities: 'סחורות',
  bonds_cpi_corp: 'אג"ח צמוד מדד קונצרני',
  bonds_cpi_gov: 'אג"ח צמוד מדד ממשלתי',
  bonds_foreign: 'אג"ח צמוד מט"ח',
  bonds_nis_corp: 'אג"ח שקלי קונצרני',
  bonds_nis_gov: 'אג"ח שקלי ממשלתי',
  money_market: 'מ.ק.מ',
  cash_ils: 'עו"ש ופק"ם',
  cash_fx: 'עו"ש מט"ח',
  alternatives: 'אלטרנטיבי',
};

interface Position {
  security_name: string;
  isin: string | null;
  quantity: number | null;
  price: number | null;
  value: number | null;
  sleeve: string | null;
}

interface FileDetail {
  file_id: string;
  filename: string;
  fund: string;
  has_pdf: boolean;
  extraction: {
    /** Which model produced this — the first thing you need when comparing runs. */
    provider: string | null;
    model: string | null;
    owner_name: string | null;
    report_date: string | null;
    total_value: number | null;
    deposits: number | null;
    withdrawals: number | null;
    sleeves: Record<string, number | null>;
    positions: Position[];
    positions_sum: number | null;
  } | null;
}

export function PdfCompareView({
  batchId,
  file,
  onClose,
}: {
  batchId: string;
  file: BatchFile;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<FileDetail | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.get<FileDetail>(`/admin/ingest/batch/${batchId}/file/${file.file_id}/detail`)
      .then((d) => live && setDetail(d))
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [batchId, file.file_id]);

  // The document is fetched rather than linked, so it carries the same auth as
  // every other call. The object URL is released when the view closes.
  useEffect(() => {
    let live = true;
    let url: string | null = null;
    api.blob(`/admin/ingest/batch/${batchId}/file/${file.file_id}/pdf`)
      .then((b) => {
        if (!live) return;
        url = URL.createObjectURL(b);
        setPdfUrl(url);
      })
      .catch(() => { /* has_pdf drives the fallback message */ });
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [batchId, file.file_id]);

  const x = detail?.extraction ?? null;

  return (
    <Modal open title={`${file.fund} — בדיקה מול הקובץ`} size="xl" onClose={onClose}>
      <div className="grid gap-4 lg:grid-cols-2">
        {/* The document. An <object> degrades to its children when the browser
            has no PDF viewer, instead of showing an empty grey box. */}
        <div className="min-h-[24rem] overflow-hidden rounded-lg border border-border bg-muted/30 lg:h-[68vh]">
          {pdfUrl ? (
            <object
              data={pdfUrl}
              type="application/pdf"
              className="h-full min-h-[24rem] w-full"
              aria-label={`הקובץ ${file.filename}`}
            >
              <p className="p-4 text-sm text-muted-foreground">
                הדפדפן לא מציג PDF כאן.{' '}
                <a href={pdfUrl} target="_blank" rel="noreferrer" className="underline">
                  פתחו את הקובץ בלשונית חדשה
                </a>
              </p>
            </object>
          ) : detail?.has_pdf ? (
            <div className="p-4"><Loading /></div>
          ) : (
            <p className="p-4 text-sm text-muted-foreground">
              הקובץ כבר אינו זמין לצפייה (נשמר רק עד לפרסום).
            </p>
          )}
        </div>

        {/* What was read from it. */}
        <div className="grid gap-4 lg:h-[68vh] lg:overflow-y-auto lg:pe-1">
          {error && <p className="text-sm text-destructive">{error}</p>}
          {!detail && !error && <Loading />}
          {x && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {x.model ? <Ltr>{x.model}</Ltr> : 'המודל לא נרשם'}
                </span>
                <CopyJson extraction={x} filename={file.filename} />
              </div>
              <Headline x={x} />
              <Section title="לפי אפיק">
                <Table minWidth="18rem">
                  <THeadRow>
                    <TH>אפיק</TH>
                    <TH align="end">שווי</TH>
                  </THeadRow>
                  <tbody>
                    {Object.entries(x.sleeves)
                      .filter(([, v]) => v !== null)
                      .map(([k, v]) => (
                        <TR key={k}>
                          <TD>{SLEEVE_HE[k] ?? k}</TD>
                          <TD align="end" className="tabular-nums">{fmtNum(v ?? 0)}</TD>
                        </TR>
                      ))}
                  </tbody>
                </Table>
              </Section>

              <Section title={`ניירות ערך (${x.positions.length})`}>
                {x.positions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">לא חולצו ניירות ערך מהקובץ.</p>
                ) : (
                  <Table minWidth="26rem">
                    <THeadRow>
                      <TH>נייר</TH>
                      <TH align="end">כמות</TH>
                      <TH align="end">שער</TH>
                      <TH align="end">שווי</TH>
                    </THeadRow>
                    <tbody>
                      {x.positions.map((p, i) => (
                        <TR key={`${p.security_name}|${p.isin ?? i}`}>
                          <TD className="max-w-[12rem] truncate" title={p.security_name}>
                            {p.security_name}
                          </TD>
                          <TD align="end" className="tabular-nums text-muted-foreground">
                            {p.quantity == null ? '—' : fmtNum(p.quantity)}
                          </TD>
                          <TD align="end" className="tabular-nums text-muted-foreground">
                            {p.price == null ? '—' : fmtNum(p.price)}
                          </TD>
                          <TD align="end" className="tabular-nums">
                            {p.value == null ? '—' : fmtNum(p.value)}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * The whole extraction, as the model returned it.
 *
 * The tables above are the readable view; this is the one you paste into a bug
 * report when a file comes out wrong. Everything is included — the sleeve
 * totals, every position, and which model produced them — because the useful
 * question is usually "what did it NOT see", and a curated view cannot answer
 * that.
 *
 * The clipboard is refused more often than it looks: an unfocused window, an
 * insecure origin, a browser that just says no. A button that quietly does
 * nothing is the worst outcome, so a refusal shows the text to copy by hand.
 */
function CopyJson({
  extraction,
  filename,
}: {
  extraction: NonNullable<FileDetail['extraction']>;
  filename: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const json = JSON.stringify({ filename, ...extraction }, null, 2);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      setState('failed');
    }
  };

  if (state === 'failed') {
    return (
      <div className="w-full">
        <p className="text-xs text-muted-foreground">
          הדפדפן לא איפשר העתקה אוטומטית — סמנו את הטקסט והעתיקו:
        </p>
        <textarea
          readOnly
          rows={8}
          value={json}
          onFocus={(e) => e.currentTarget.select()}
          dir="ltr"
          className="mt-1 w-full rounded-md border border-border bg-background p-2 font-mono text-xs"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
    >
      {state === 'copied' ? 'הועתק ✓' : 'העתקת JSON'}
    </button>
  );
}

function Headline({ x }: { x: NonNullable<FileDetail['extraction']> }) {
  // Positions that do not add up to the stated total is the single most useful
  // signal that a page was misread, so it is stated rather than left to arithmetic.
  const diff =
    x.positions_sum !== null && x.total_value
      ? Math.abs(x.positions_sum - x.total_value) / x.total_value
      : null;
  return (
    <div className="grid gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm">
      <Row label="בעל התיק" value={x.owner_name ?? '—'} />
      <Row label="תאריך דוח" value={<Ltr>{x.report_date ?? '—'}</Ltr>} />
      <Row label='סה"כ' value={<span className="tabular-nums font-semibold">{fmtNum(x.total_value ?? 0)}</span>} />
      <Row
        label="סכום ניירות הערך"
        value={
          <span className={diff !== null && diff > 0.005 ? 'tabular-nums text-warning' : 'tabular-nums'}>
            {x.positions_sum === null ? '—' : fmtNum(x.positions_sum)}
            {diff !== null && diff > 0.005 && ` (פער ${(diff * 100).toFixed(2)}%)`}
          </span>
        }
      />
      <Row
        label="הפקדות / משיכות"
        value={
          x.deposits === null && x.withdrawals === null
            ? <span className="text-muted-foreground">לא נמצאו בקובץ</span>
            : <span className="tabular-nums">+{fmtNum(x.deposits ?? 0)} / −{fmtNum(x.withdrawals ?? 0)}</span>
        }
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}
